import {
  Alert, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, InputAdornment, Stack, TextField, Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  Edit, SaveButton, TabbedForm, TextInput, NumberInput, SelectInput,
  Toolbar, TopToolbar, useRecordContext, useNotify, useRedirect, type RaRecord
} from 'react-admin';
import { BackLink } from './back';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import { callAction } from './dataProvider';
import { ClassificationTab } from './productClassification';
import { ProductImages } from './productImages';
import { SeoFields } from './seoFields';

type Product = RaRecord & {
  code: string | null; heading: string | null; type: string | null; repassed: boolean;
  seoUri?: string | null; seoPath?: string | null;
  fileIdentifier?: string | null; fileIdentifier2?: string | null; fileIdentifier3?: string | null;
};

interface GroupPrice {
  groupId: number; name: string;
  originalDiscountPercent: number; renewalDiscountPercent: number;
  price: number | null;
}

interface Prices {
  points: number; normal: number | null; minimal: number | null; buyout: number | null;
  groups: GroupPrice[];
}

/**
 * Ceny — the prices tab.
 *
 * The three base prices, then one box per user group. Each group shows its two
 * percentages, because an explicit price here REPLACES them: without seeing
 * both, someone typing in a box cannot tell what the customer would otherwise
 * have paid.
 *
 * Its own form and its own save, not part of the product form: prices are rows
 * in `price` and `product_user_group_lookup`, not columns on a product, and
 * saving a name should not silently rewrite money.
 *
 * An empty box means "no explicit price" and is sent as null, which deletes
 * the override so the group falls back to its percentage. That is why blanks
 * are distinguished from zero rather than coerced - a zero here would price the
 * product free.
 */
function PricesTab() {
  const record = useRecordContext<Product>();
  const notify = useNotify();
  const [prices, setPrices] = useState<Prices | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (record == null) return;
    callAction(`products/${record.id}/prices`, 'GET' as never)
      .then(value => {
        const loaded = value as Prices;
        setPrices(loaded);
        setDraft({
          normal: text(loaded.normal),
          minimal: text(loaded.minimal),
          buyout: text(loaded.buyout),
          points: String(loaded.points),
          ...Object.fromEntries(loaded.groups.map(group => [`g${group.groupId}`, text(group.price)]))
        });
      })
      .catch(() => notify('Ceny sa nepodarilo načítať.', { type: 'warning' }));
  };

  useEffect(load, [record?.id]);

  if (prices == null || record == null) return <Typography variant="body2">Načítavajú sa ceny…</Typography>;

  const isRefill = record.repassed === true;
  const set = (key: string, value: string) => setDraft(current => ({ ...current, [key]: value }));

  const save = async () => {
    const normal = number(draft['normal']);
    const minimal = number(draft['minimal']);

    // Caught here as well as in the API, because the storefront floors every
    // price at minimal: a standard price below it would simply never be charged.
    if (normal != null && minimal != null && normal < minimal) {
      notify('Štandardná cena je nižšia než minimálna — zákazník by aj tak zaplatil minimálnu.', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await callAction(`products/${record.id}/prices`, 'PUT', {
        normal,
        minimal,
        buyout: number(draft['buyout']),
        points: number(draft['points']) ?? 0,
        groupPrices: Object.fromEntries(
          prices.groups.map(group => [group.groupId, number(draft[`g${group.groupId}`])])
        )
      });
      notify('Ceny uložené.', { type: 'success' });
      load();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Ceny sa nepodarilo uložiť.', { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <PriceBox label="Štandardná cena" value={draft['normal'] ?? ''} onChange={value => set('normal', value)} />
        <PriceBox label="Minimálna cena" value={draft['minimal'] ?? ''} onChange={value => set('minimal', value)} />
        <PriceBox label="Výkupná cena" value={draft['buyout'] ?? ''} onChange={value => set('buyout', value)} />
        <TextField
          label="Body"
          size="small"
          value={draft['points'] ?? ''}
          onChange={event => set('points', event.target.value)}
          sx={{ width: 110 }}
        />
      </Stack>

      <Alert severity="info" variant="outlined">
        Skupinová zľava platí len na <strong>náplne</strong> — tlačiarne ju nikdy nedostanú. Tento
        produkt je {isRefill ? 'renovovaný, takže platí percento pre renováciu' : 'originál, takže platí percento pre originál'}.
        Pevná cena nižšie nahrádza percento úplne. Prázdne políčko znamená žiadnu pevnú cenu —
        použije sa percento.
      </Alert>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">Ceny podľa skupiny</Typography>
          <Stack sx={{ mt: 1 }} spacing={1}>
            {prices.groups.map(group => {
              const percent = isRefill ? group.renewalDiscountPercent : group.originalDiscountPercent;
              const base = number(draft['normal']) ?? prices.normal;
              const wouldPay = base == null ? null : base - (percent / 100) * base;

              return (
                <Stack key={group.groupId} direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{group.name}</Typography>
                    <Chip size="small" variant="outlined" label={`o: ${group.originalDiscountPercent}, r: ${group.renewalDiscountPercent}`} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {wouldPay == null ? '—' : `bez pevnej ceny ${wouldPay.toFixed(2)} €`}
                    </Typography>
                    <PriceBox
                      label="Pevná cena"
                      value={draft[`g${group.groupId}`] ?? ''}
                      onChange={value => set(`g${group.groupId}`, value)}
                    />
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={() => void save()} disabled={saving}>
          Uložiť ceny
        </Button>
        <Button onClick={load} disabled={saving}>Zahodiť zmeny</Button>
      </Stack>
    </Stack>
  );
}

function PriceBox({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <TextField
      label={label}
      size="small"
      value={value}
      onChange={event => onChange(event.target.value)}
      InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
      sx={{ width: 150 }}
    />
  );
}

/** Empty stays empty: it means "no price", which is different from zero. */
function text(value: number | null): string {
  return value == null ? '' : String(value);
}

function number(value: string | undefined): number | null {
  if (value == null) return null;
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

interface Sale { id: number; price: number; from: string; to: string; active: boolean }

/**
 * Akcia — time-boxed sale prices.
 *
 * Sales are kept after they expire, so this is a history as much as a
 * schedule; every one of the 327 in production has already ended. A sale at or
 * below the minimal price is refused by the API, because the storefront floors
 * every price at minimal and the discount would never be shown.
 */
function SalesTab() {
  const record = useRecordContext<Product>();
  const notify = useNotify();
  const [sales, setSales] = useState<Sale[] | null>(null);
  const [price, setPrice] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (record == null) return;
    callAction(`products/${record.id}/sales`, 'GET' as never)
      .then(value => setSales(value as Sale[]))
      .catch(() => notify('Akcie sa nepodarilo načítať.', { type: 'warning' }));
  };

  useEffect(load, [record?.id]);

  if (sales == null || record == null) return <Typography variant="body2">Načítava sa…</Typography>;

  const add = async () => {
    const amount = number(price);
    if (amount == null) { notify('Zadajte akciovú cenu.', { type: 'warning' }); return; }
    if (from === '' || to === '') { notify('Zadajte obdobie akcie.', { type: 'warning' }); return; }
    if (new Date(from) > new Date(to)) { notify('Akcia sa nemôže skončiť skôr, než začne.', { type: 'warning' }); return; }

    setBusy(true);
    try {
      await callAction(`products/${record.id}/sales`, 'POST', { price: amount, from, to });
      notify('Akcia pridaná.', { type: 'success' });
      setPrice(''); setFrom(''); setTo('');
      load();
    } catch (error) {
      // The API refuses a sale at or below the minimal price, and its message
      // explains why - the storefront would floor it and never show a discount.
      notify(error instanceof Error ? error.message : 'Akciu sa nepodarilo pridať.', { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sale: Sale) => {
    setBusy(true);
    try {
      await callAction(`products/${record.id}/sales/${sale.id}`, 'DELETE');
      notify('Akcia zrušená.', { type: 'info' });
      load();
    } catch {
      notify('Akciu sa nepodarilo zrušiť.', { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">Akciové ceny</Typography>
          {sales.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Žiadne akcie.</Typography>
          ) : (
            <Stack sx={{ mt: 1 }} spacing={0.5}>
              {sales.map(sale => (
                <Stack key={sale.id} direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">{sale.price} €</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sale.from.slice(0, 10)} → {sale.to.slice(0, 10)}
                  </Typography>
                  {sale.active
                    ? <Chip size="small" color="success" label="Prebieha" />
                    : <Chip size="small" variant="outlined" label="Ukončená" />}
                  <IconButton
                    size="small"
                    aria-label="Zrušiť akciu"
                    disabled={busy}
                    onClick={() => void remove(sale)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">Nová akcia</Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <TextField
              label="Akciová cena"
              size="small"
              value={price}
              onChange={event => setPrice(event.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
              sx={{ width: 160 }}
            />
            <TextField
              label="Od"
              type="date"
              size="small"
              value={from}
              onChange={event => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Do"
              type="date"
              size="small"
              value={to}
              onChange={event => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" onClick={() => void add()} disabled={busy}>Pridať</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Akcia musí byť nižšia než minimálna cena produktu — inak by ju web aj tak nezobrazil.
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
}

/**
 * The product's own search metadata.
 *
 * Stored against its address on the storefront, which is built from the type,
 * the producers and the slug - so the address is shown here rather than left
 * implicit: change the slug on the Detaily tab and the metadata follows it,
 * which is worth being able to see.
 *
 * No product in this shop has metadata of its own yet; every one of them
 * currently shows the site-wide default title and description.
 */
function ProductSeoTab() {
  const record = useRecordContext<Product>();
  if (record == null) return null;

  if (record.seoUri == null) {
    return (
      <Alert severity="warning" variant="outlined">
        Tento produkt nemá adresu na webe (chýba mu slug), takže preň nie je kam SEO uložiť.
        Doplňte slug na karte Detaily.
      </Alert>
    );
  }

  return (
    <Stack sx={{ width: '100%' }}>
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Platí pre adresu <strong>{record.seoPath}</strong>. Ak necháte polia prázdne, použije sa
        všeobecný názov a popis e-shopu — tak je to teraz pri všetkých produktoch.
      </Alert>
      <SeoFields showNotice={false} />
    </Stack>
  );
}

/**
 * Removal, behind a dialog that names the product.
 *
 * It sits in the page header rather than next to Save: saving is what this
 * form is for, and a red button beside it is one mis-click from a product
 * vanishing off the shop. Up here it is reachable from every tab and nowhere
 * near the one people press all day.
 *
 * The API only flags the row, so orders keep their lines and the product can
 * be restored by hand - but not from here, which is what the dialog says.
 */
function DeleteProductButton() {
  const record = useRecordContext<Product>();
  const notify = useNotify();
  const redirect = useRedirect();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  if (record == null) return null;

  const name = record.code ?? record.heading ?? `#${record.id}`;

  const remove = async () => {
    setBusy(true);
    try {
      await callAction(`products/${record.id}`, 'DELETE');
      notify('Produkt bol zmazaný.', { type: 'info' });
      setAsking(false);
      redirect('list', 'products');
    } catch (error) {
      notify(error instanceof Error && error.message ? error.message : 'Produkt sa nepodarilo zmazať.', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setAsking(true)}>
        Zmazať produkt
      </Button>

      <Dialog open={asking} onClose={() => busy || setAsking(false)}>
        <DialogTitle>Zmazať produkt {name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Produkt <b>{record.heading ?? name}</b> zmizne z e-shopu aj z tohto zoznamu. Existujúce
            objednávky si jeho názov a kód ponechajú. Z administrácie sa zmazanie nedá vrátiť späť.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsking(false)} disabled={busy}>Zrušiť</Button>
          <Button onClick={remove} color="error" variant="contained" disabled={busy}>Zmazať</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function ProductEdit() {
  return (
    <Edit
      title="Produkt"
      mutationMode="pessimistic"
      /* The only way back to the list was the browser's own back button, which
         is easy to miss on a tabbed form - each tab is a history entry, so
         going back from the fourth tab takes four presses. react-admin keeps
         the list's filters and page in its store, so this returns to the list
         exactly as it was left. */
      actions={
        <TopToolbar sx={{ justifyContent: 'space-between' }}>
          <BackLink />
          <DeleteProductButton />
        </TopToolbar>
      }
    >
      <StorefrontNotice />

      {/* A save toolbar, but only the Detaily tab writes through the form -
          prices and classification have their own endpoints and their own
          controls, because they are not fields on a product row. */}
      <TabbedForm toolbar={<Toolbar><SaveButton /></Toolbar>}>
        <TabbedForm.Tab label="Detaily">
          <ProductImages />
          <Stack direction="row" spacing={2}>
            <TextInput source="code" label="Kód" />
            <TextInput source="ean" label="EAN" helperText="Čiarový kód. Zobrazuje sa na karte produktu na e-shope." />
            <TextInput source="tag" label="Tag" />
          </Stack>
          <TextInput source="heading" label="Názov" fullWidth />
          <TextInput source="slug" fullWidth helperText="Zmena tohto textu zmení adresu produktu na webe." />
          <SelectInput
            source="type"
            choices={[
              { id: 'toner', name: 'Náplň do tlačiarne' },
              { id: 'printer', name: 'Tlačiareň' },
              { id: 'other', name: 'Iné' }
            ]}
            helperText="Skupinovú zľavu dostávajú len náplne."
          />
          <SelectInput
            source="warehouseStatus"
            label="Dostupnosť"
            choices={[
              'skladom', 'do 3 dní', 'do týždňa', 'do 2 týždňov',
              'do mesiaca', 'viac ako mesiac', 'info v obchode'
            ].map(value => ({ id: value, name: value }))}
          />
          <NumberInput source="points" />
          <TextInput source="excerpt" multiline rows={2} fullWidth />
          <TextInput source="body" multiline rows={6} fullWidth label="Popis" />
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Ceny">
          <PricesTab />
        </TabbedForm.Tab>

        <TabbedForm.Tab label="SEO">
          <ProductSeoTab />
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Zaradenie">
          <ClassificationTab />
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Akcia">
          <SalesTab />
        </TabbedForm.Tab>
      </TabbedForm>
    </Edit>
  );
}

/**
 * Whether customers can actually find this product.
 *
 * Every field on this form can be filled in correctly and the product still
 * appear nowhere: the shop needs a normal price (its query inner-joins price,
 * so without one the product's own page answers 404) and a category (every tab
 * and filtered search selects on the category lookup). Neither lives on the
 * product row, so nothing else on this screen hints at it.
 *
 * Both arrived as bug reports - "it stayed in the admin but is not on the
 * site", then "it cannot be found in the admin either" - which is why this
 * says it plainly instead of leaving it to be deduced.
 */
function StorefrontNotice() {
  const record = useRecordContext<{ storefront?: {
    visible: boolean; missingPrice: boolean; missingCategory: boolean;
  } }>();

  const state = record?.storefront;
  if (state == null) return null;

  if (state.visible) {
    return (
      <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
        Produkt sa zobrazuje na e-shope.
      </Alert>
    );
  }

  const missing = [
    state.missingPrice ? 'štandardná cena (záložka Ceny)' : null,
    state.missingCategory ? 'zaradenie do kategórie (záložka Zaradenie)' : null
  ].filter((item): item is string => item != null);

  return (
    <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
      <b>Tento produkt sa zákazníkom nezobrazuje.</b> Chýba: {missing.join(' a ')}.
      {state.missingCategory && ' Bez zaradenia sa produkt nenájde ani vo vyhľadávaní na e-shope, ani v záložkách v administrácii.'}
    </Alert>
  );
}
